import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const ROLES = ["Medico", "Infermiere"];

export function SanitarioDialog({ open, sanitario, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", qualifications: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(sanitario
        ? { name: sanitario.name, email: sanitario.email || "", phone: sanitario.phone || "", qualifications: sanitario.qualifications.filter((q) => ROLES.includes(q)) }
        : { name: "", email: "", phone: "", qualifications: [] });
    }
  }, [open, sanitario]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggle = (q) => set("qualifications", form.qualifications.includes(q) ? form.qualifications.filter((x) => x !== q) : [...form.qualifications, q]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = sanitario ? await api.put(`/sanitari/${sanitario.id}`, form) : await api.post("/sanitari", form);
      toast.success(sanitario ? "Dati aggiornati" : "Sanitario aggiunto");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const emailOptional = !sanitario || sanitario.external;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sanitario-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{sanitario ? "Modifica sanitario" : "Aggiungi sanitario"}</DialogTitle>
          <DialogDescription>
            {sanitario && !sanitario.external ? "Volontario registrato all'app." : "Sanitario inserito a mano: non accede all'app, lo assegni tu ai servizi."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nome e cognome</Label>
            <Input data-testid="sanitario-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Email {emailOptional && <span className="text-muted-foreground font-normal">(facoltativa)</span>}</Label>
            <Input type="email" data-testid="sanitario-email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Telefono</Label>
            <Input type="tel" data-testid="sanitario-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div className="flex gap-3">
            {ROLES.map((q) => (
              <label key={q} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                <Checkbox data-testid={`sanitario-qual-${q}`} checked={form.qualifications.includes(q)} onCheckedChange={() => toggle(q)} />
                <span>{q}</span>
              </label>
            ))}
          </div>
          {sanitario && form.qualifications.length === 0 && (
            <p className="text-xs text-destructive">Senza qualifica Medico o Infermiere uscirà dall'elenco e dai servizi futuri come sanitario.</p>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="sanitario-save" onClick={save} disabled={saving || !form.name.trim()} className="rounded-full font-semibold">
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
